/**
 * FieldTooltip — small (?) icon next to a field label that opens a popover
 * with a short description and 1-3 example values.
 *
 * Behaviour:
 *   - Hover (desktop) opens after a short delay; mouse-leave closes after a delay.
 *   - Click / Enter / Space toggles open (works on touch + keyboard).
 *   - Click outside or Escape closes.
 *   - Popover stays inside the viewport on small screens by clamping its
 *     horizontal position via inline style.
 *
 * Design language: matches the rest of the portal — slate text, light
 * background, soft border, `#2CABEA` accent (the portal's brand blue).
 */

import {
  KeyboardEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

interface Props {
  description: string;
  examples?: string[];
  /** Optional aria-label override for the trigger button. */
  ariaLabel?: string;
}

const HOVER_OPEN_DELAY = 120;
const HOVER_CLOSE_DELAY = 200;

export function FieldTooltip({ description, examples, ariaLabel }: Props) {
  const [open, setOpen] = useState(false);
  const [shiftX, setShiftX] = useState(0);
  const containerRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const reactId = useId();
  const popoverId = `${reactId}-tooltip`;

  const clearTimers = () => {
    if (openTimer.current != null) {
      window.clearTimeout(openTimer.current);
      openTimer.current = null;
    }
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  // Cleanup on unmount.
  useEffect(() => () => clearTimers(), []);

  // Close on outside click / Escape while open.
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: Event) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('touchstart', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('touchstart', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Clamp popover position inside viewport when it opens or window resizes.
  useLayoutEffect(() => {
    if (!open) {
      setShiftX(0);
      return;
    }
    const reposition = () => {
      const pop = popoverRef.current;
      if (!pop) return;
      // Reset before measuring to get the natural rect.
      pop.style.transform = 'translateX(0)';
      const rect = pop.getBoundingClientRect();
      const margin = 8;
      let dx = 0;
      if (rect.left < margin) dx = margin - rect.left;
      else if (rect.right > window.innerWidth - margin) dx = window.innerWidth - margin - rect.right;
      setShiftX(dx);
    };
    reposition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [open]);

  const onMouseEnter = useCallback(() => {
    clearTimers();
    openTimer.current = window.setTimeout(() => setOpen(true), HOVER_OPEN_DELAY);
  }, []);

  const onMouseLeave = useCallback(() => {
    clearTimers();
    closeTimer.current = window.setTimeout(() => setOpen(false), HOVER_CLOSE_DELAY);
  }, []);

  const onTriggerClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    clearTimers();
    setOpen((prev) => !prev);
  };

  const onTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      clearTimers();
      setOpen((prev) => !prev);
    }
  };

  return (
    <span
      ref={containerRef}
      className="relative inline-flex items-center align-middle ml-1"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-label={ariaLabel ?? 'Field help'}
        aria-describedby={open ? popoverId : undefined}
        aria-expanded={open}
        onClick={onTriggerClick}
        onKeyDown={onTriggerKeyDown}
        className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-100 text-slate-600 text-[11px] font-bold leading-none border border-slate-300 hover:bg-[#EBF7FD] hover:text-[#0B2341] hover:border-[#2CABEA] focus:outline-none focus:ring-2 focus:ring-[#2CABEA] focus:ring-offset-1 transition-colors"
      >
        ?
      </button>
      {open && (
        <div
          ref={popoverRef}
          id={popoverId}
          role="tooltip"
          style={{ transform: `translateX(${shiftX}px)` }}
          className="absolute left-1/2 -translate-x-1/2 top-full mt-2 z-50 w-[min(20rem,calc(100vw-1rem))] rounded-lg border border-slate-200 bg-white shadow-lg p-3 text-left"
        >
          <p className="text-sm text-slate-700 leading-snug">{description}</p>
          {examples && examples.length > 0 && (
            <div className="mt-2 pt-2 border-t border-slate-100">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">
                {examples.length === 1 ? 'Example' : 'Examples'}
              </p>
              <ul className="space-y-0.5">
                {examples.map((ex, i) => (
                  <li
                    key={i}
                    className="text-xs text-[#0B2341] font-mono bg-[#EBF7FD] rounded px-2 py-1"
                  >
                    {ex}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </span>
  );
}
